import React, {useEffect, useState} from "react";
import {
    assessmentsAgOverrideFormatting,
    assessmentsAgRowFormatting,
    assessmentsOverrideFormatting,
    calculateAgriculturalValue,
    calculateAppraisedValue,
    calculateAssessedValue,
    calculateLandTotalValue,
    exemptionsList,
    isAgExemptionExist
} from "../../../../helpers/utils/OrderDetailUtils";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { getCurrentTaxYear } from "../../../../helpers/utils/Utils";
import { useGetPdfSettingsByCustomerIdQuery } from "../../../customer/pdfSettingsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { AssessmentYearRow } from "../UtilComponents/AssessmentYearRow";
import AssessmentsRowFormatting from "../UtilComponents/AssessmentsRowFormatting";

interface AssessmentInfoSectionProps {
    curAccount: any;
    setCertificate: any;
    certificate: any;
    setName: any;
    setIsSavedResult: any;
    accounts: any[];
    setAccounts: any;
    editable: boolean;
    setProductTypeInvisible: any;
    setRender: any;
    setUpdatedAccountIndexList: any;
    showAccount: number;
    customerId: any
}

const AssessmentInfoSection: React.FC<AssessmentInfoSectionProps> = ({
    curAccount,
    setCertificate,
    certificate,
    setName,
    setIsSavedResult,
    accounts,
    setAccounts,
    editable,
    setProductTypeInvisible,
    setRender,
    setUpdatedAccountIndexList,
    showAccount,
    customerId
}) => {
    const [assessmentIndex, setAssessmentIndex] = useState<number | null>(null);
    const [overrideValue, setOverrideValue] = useState<boolean>(false);
    const [firstYearHasAg, setFirstYearHasAg] = useState<boolean | null>(null);
    const [secondYearHasAg, setSecondYearHasAg] = useState<boolean | null>(null);
    const [showWee, setShowWee] = useState<boolean>(true);
    const { data: enums } = useGetEnumsQuery("");
    const assessmentList = certificate?.accounts?.[showAccount]?.assessments;
    const certified = Array.isArray(assessmentList) && assessmentList[0]?.['certified'] === true;
    const pdfSettings = useGetPdfSettingsByCustomerIdQuery(customerId ? customerId : skipToken);
    const filteredExemptions = enums?.["Exemption"]?.filter(
        (item:any) => !["DV1030", "DV3150", "DV5170", "DV71100","TOT"].includes(item.value)
    );


    useEffect(() => {
        if (firstYearHasAg || secondYearHasAg || overrideValue) {
            setUpdatedAccountIndexList((prev: any) => [...prev, showAccount]);
        }
    }, [firstYearHasAg, secondYearHasAg, overrideValue]);

    const recalculateForAgChanges = (curAccount: any, showAccount: any, assessmentIndex: any, setRender: any, setCertificate: any) => {
        let assessment = curAccount?.assessments[assessmentIndex];
        calculateAgriculturalValue(showAccount, assessment, setRender, setCertificate).then();
        calculateLandTotalValue(showAccount, assessment, setRender, setCertificate).then();
        calculateAssessedValue(showAccount, assessment, setRender, setCertificate).then();
        calculateAppraisedValue(showAccount, assessment, setRender, setCertificate).then();
    };

    useEffect(() => {
        if (!overrideValue) return;
        recalculateForAgChanges(curAccount, showAccount, assessmentIndex, setRender, setCertificate);
        setIsSavedResult(false);
        setOverrideValue(false);
    }, [overrideValue]);

    const assessmentJson = JSON.stringify(curAccount?.assessments);

    useEffect(() => {

        if (!curAccount?.assessments) return;
        setFirstYearHasAg(isAgExemptionExist(curAccount, 0));
        setSecondYearHasAg(isAgExemptionExist(curAccount, 1));
    }, [assessmentJson]);

    const clearAgValues = (curAccount: any, index: number) => {
        const updatedAssessment = {
            ...curAccount.assessments[index],
            agTotal: null,
            agMarketValue: null,
            agTimberValue: null,
            agLossValue: null,
            overrideAgValue: null,
            overrideLandTotalValue: null,
        };

        const updatedAssessments = [...curAccount.assessments];
        updatedAssessments[index] = updatedAssessment;

        const updatedAccount = {
            ...curAccount,
            assessments: updatedAssessments,
        };

        const updatedAccounts = [...certificate.accounts];
        updatedAccounts[showAccount] = updatedAccount;

        setCertificate({
            ...certificate,
            accounts: updatedAccounts,
        });
    };

    useEffect(() => {
        if (firstYearHasAg === false) {
            clearAgValues(curAccount, 0);
            recalculateForAgChanges(curAccount, showAccount, 0, setRender, setCertificate);

        }

    }, [firstYearHasAg]);

    useEffect(() => {
        if (secondYearHasAg === false) {
            clearAgValues(curAccount, 1);
            recalculateForAgChanges(curAccount, showAccount, 1, setRender, setCertificate);
        }
    }, [secondYearHasAg]);


    return (
        <div className="h-full">
            <div className="border-l border-highlight-5 h-full property_info table_data">
                <table
                    className="text-sm w-full [&_td]:border [&_td]:border-surface-5 p-[6px] text-left account_information">
                    <thead>
                        <tr>
                            <th className="bg-highlight-5 text-text-0 relative pl-2 whitespace-nowrap">Assessment Info</th>
                            {[0, 1].map((i) => (
                                <th key={i} className="bg-highlight-5 text-text-0 relative pl-2 text-center">
                                    <AssessmentYearRow
                                        curAccount={curAccount}
                                        index={i}
                                        name="taxYear"
                                        setName={setName}
                                        editable={editable}
                                        setIsSavedResult={setIsSavedResult}
                                        setCertificate={setCertificate}

                                    />
                                </th>
                            ))}
                        </tr>

                    </thead>
                    <tbody>

                        <tr>


                            <td className="first_td pl-3">Land</td>
                            <td>
                                <AssessmentsRowFormatting
                                    setCertificate={setCertificate}
                                    certificate={certificate}
                                    showAccount={showAccount}
                                    index={0}
                                    name="landMarketValue"
                                    setName={setName}
                                    setProductTypeInvisible={setProductTypeInvisible}
                                    editable={editable}
                                    setIsSavedResult={setIsSavedResult}
                                />
                            </td>

                            <td>
                                <AssessmentsRowFormatting
                                    setCertificate={setCertificate}
                                    certificate={certificate}
                                    showAccount={showAccount}
                                    index={1}
                                    name="landMarketValue"
                                    setName={setName}
                                    setProductTypeInvisible={setProductTypeInvisible}
                                    editable={editable}
                                    setIsSavedResult={setIsSavedResult}
                                />
                            </td>
                        </tr>

                        {(firstYearHasAg || secondYearHasAg) &&
                            <>

                                <tr>
                                    <td className="first_td pl-3">Ag Market</td>
                                    <td>
                                        {assessmentsAgRowFormatting(setAssessmentIndex, setOverrideValue, curAccount, 0, editable, certificate, setCertificate, "agMarketValue", setIsSavedResult, showAccount, setUpdatedAccountIndexList)}
                                    </td>
                                    <td>
                                        {assessmentsAgRowFormatting(setAssessmentIndex, setOverrideValue, curAccount, 1, editable, certificate, setCertificate, "agMarketValue", setIsSavedResult, showAccount, setUpdatedAccountIndexList)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="first_td pl-3">Ag Timber</td>
                                    <td>
                                        {assessmentsAgRowFormatting(setAssessmentIndex, setOverrideValue, curAccount, 0, editable, certificate, setCertificate, "agTimberValue", setIsSavedResult, showAccount, setUpdatedAccountIndexList)}
                                    </td>
                                    <td>
                                        {assessmentsAgRowFormatting(setAssessmentIndex, setOverrideValue, curAccount, 1, editable, certificate, setCertificate, "agTimberValue", setIsSavedResult, showAccount, setUpdatedAccountIndexList)}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="first_td pl-3">Ag Loss</td>
                                    <td>
                                        {assessmentsAgRowFormatting(setAssessmentIndex, setOverrideValue, curAccount, 0, editable, certificate, setCertificate, "agLossValue", setIsSavedResult, showAccount, setUpdatedAccountIndexList)}
                                    </td>
                                    <td>
                                        {assessmentsAgRowFormatting(setAssessmentIndex, setOverrideValue, curAccount, 1, editable, certificate, setCertificate, "agLossValue", setIsSavedResult, showAccount, setUpdatedAccountIndexList)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="first_td pl-3">Ag Total</td>
                                    <td title={'(AgMarketValue + AgTimberValue) - AgLossValue'}>
                                        {assessmentsAgOverrideFormatting(certificate, setCertificate, showAccount, 0, 'agriculturalValue', true, setName, setProductTypeInvisible, editable, 'overrideAgValue', setAssessmentIndex, setIsSavedResult, setRender)}
                                    </td>
                                    <td title={'(AgMarketValue + AgTimberValue) - AgLossValue'}>
                                        {assessmentsAgOverrideFormatting(certificate, setCertificate, showAccount, 1, 'agriculturalValue', true, setName, setProductTypeInvisible, editable, 'overrideAgValue', setAssessmentIndex, setIsSavedResult, setRender)}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="first_td pl-3">Land Total</td>
                                    <td title={'LandMarketValue + (AgMarketValue + AgTimberValue)'}>
                                        {assessmentsAgOverrideFormatting(certificate, setCertificate, showAccount, 0, 'landTotalValue', true, setName, setProductTypeInvisible, editable, 'overrideLandTotalValue', setAssessmentIndex, setIsSavedResult, setRender)}
                                    </td>
                                    <td title={'LandMarketValue + (AgMarketValue + AgTimberValue)'}>
                                        {assessmentsAgOverrideFormatting(certificate, setCertificate, showAccount, 1, 'landTotalValue', true, setName, setProductTypeInvisible, editable, 'overrideLandTotalValue', setAssessmentIndex, setIsSavedResult, setRender)}
                                    </td>
                                </tr>
                            </>
                        }

                        <tr>
                            <td className="pl-3">
                                Improvement
                            </td>


                            <td>
                                <AssessmentsRowFormatting
                                    setCertificate={setCertificate}
                                    certificate={certificate}
                                    showAccount={showAccount}
                                    index={0}
                                    name="improvementMarketValue"
                                    setName={setName}
                                    setProductTypeInvisible={setProductTypeInvisible}
                                    editable={editable}
                                    setIsSavedResult={setIsSavedResult}
                                />
                            </td>

                            <td>
                                <AssessmentsRowFormatting
                                    setCertificate={setCertificate}
                                    certificate={certificate}
                                    showAccount={showAccount}
                                    index={1}
                                    name="improvementMarketValue"
                                    setName={setName}
                                    setProductTypeInvisible={setProductTypeInvisible}
                                    editable={editable}
                                    setIsSavedResult={setIsSavedResult}
                                />
                            </td>

                        </tr>
                        <tr className="readonly_bg">
                            <td className="pl-3">
                                Appraised
                            </td>
                            <td title={'LandTotal + ImprovementMarketValue'}>
                                {assessmentsOverrideFormatting(curAccount, 0, 'appraisedValue', true, setName, setProductTypeInvisible, editable, "overrideAppraisedValue", null, setIsSavedResult, setRender, showAccount, setUpdatedAccountIndexList, setCertificate, certificate)}
                            </td>
                            <td title={'LandTotal + ImprovementMarketValue'}>
                                {assessmentsOverrideFormatting(curAccount, 1, 'appraisedValue', true, setName, setProductTypeInvisible, editable, "overrideAppraisedValue", null, setIsSavedResult, setRender, showAccount, setUpdatedAccountIndexList, setCertificate, certificate)}
                            </td>
                        </tr>
                        <tr>
                            <td className="pl-3">
                                10% Cap
                            </td>
                            <td>
                                <AssessmentsRowFormatting
                                    setCertificate={setCertificate}
                                    certificate={certificate}
                                    showAccount={showAccount}
                                    index={0}
                                    name="homesteadCapValue"
                                    setName={setName}
                                    setProductTypeInvisible={setProductTypeInvisible}
                                    editable={editable}
                                    setIsSavedResult={setIsSavedResult}
                                    setRender={setRender}
                                />
                            </td>

                            <td>
                                <AssessmentsRowFormatting
                                    setCertificate={setCertificate}
                                    certificate={certificate}
                                    showAccount={showAccount}
                                    index={1}
                                    name="homesteadCapValue"
                                    setName={setName}
                                    setProductTypeInvisible={setProductTypeInvisible}
                                    editable={editable}
                                    setIsSavedResult={setIsSavedResult}
                                    setRender={setRender}
                                />
                            </td>

                        </tr>
                        <tr className="readonly_bg">
                            <td className="pl-3">
                                Assessed
                            </td>
                            <td title={'(AgTotal + LandMarketValue+ ImprovementMarketValue) - HomesteadCap'}>
                                {assessmentsOverrideFormatting(curAccount, 0, 'assessedValue', true, setName, setProductTypeInvisible, editable, "overrideAssessedValue", null, setIsSavedResult, setRender, showAccount, setUpdatedAccountIndexList, setCertificate, certificate)}
                            </td>
                            <td title={'(AgTotal + LandMarketValue+ ImprovementMarketValue) - HomesteadCap'}>
                                {assessmentsOverrideFormatting(curAccount, 1, 'assessedValue', true, setName, setProductTypeInvisible, editable, "overrideAssessedValue", null, setIsSavedResult, setRender, showAccount, setUpdatedAccountIndexList, setCertificate, certificate)}
                            </td>
                        </tr>
                        <tr>
                            <td className="pl-3">
                                Exemptions
                            </td>
                            <td className="right-align">
                                {exemptionsList(!editable ? undefined : Array.isArray(enums?.["Exemption"]) && enums?.["Exemption"].length > 0 && filteredExemptions, "value", "value", "Select ...", true, true, curAccount, 0, setProductTypeInvisible, setIsSavedResult, showAccount, setUpdatedAccountIndexList,certificate,setCertificate)}
                            </td>
                            <td className="right-align">
                                {exemptionsList(!editable ? undefined : Array.isArray(enums?.["Exemption"]) && enums?.["Exemption"].length > 0 && filteredExemptions, "value", "value", "Select ...", true, true, curAccount, 1, setProductTypeInvisible, setIsSavedResult, showAccount, setUpdatedAccountIndexList,certificate,setCertificate)}
                            </td>
                        </tr>

                        <tr className="readonly_bg">
                            <td className="pl-3">
                                Certified
                            </td>
                            <td>
                                <input
                                    className="ml-2"
                                    type="checkbox"
                                    checked={certified}
                                    onClick={(e) => {
                                        const target = e.target as HTMLInputElement;
                                        if (
                                            editable &&
                                            Array.isArray(certificate.accounts?.[showAccount]?.assessments) &&
                                            certificate.accounts[showAccount].assessments[0]
                                        ) {
                                            const updatedCertificate = structuredClone(certificate);
                                            updatedCertificate.accounts[showAccount].assessments[0].certified = target.checked;

                                            setCertificate(updatedCertificate);
                                            setIsSavedResult(false);
                                            setUpdatedAccountIndexList((prev: any) =>
                                                prev.includes(showAccount) ? prev : [...prev, showAccount]
                                            );
                                        }
                                    }}

                                    disabled={!editable}
                                />

                            </td>

                            <td>
                                {certificate?.accounts[showAccount]?.assessments?.length > 1 && (
                                    <input
                                        className="ml-2"
                                        type="checkbox"
                                        checked={certificate.accounts[showAccount].assessments[1]['certified'] === true}
                                        onChange={(e) => {
                                            const target = e.target as HTMLInputElement;

                                            if (editable) {
                                                const updatedCertificate = structuredClone(certificate);
                                                const account = updatedCertificate.accounts[showAccount];

                                                if (Array.isArray(account.assessments) && account.assessments[1]) {
                                                    account.assessments[1].certified = target.checked;
                                                    setCertificate(updatedCertificate);
                                                    setIsSavedResult(false);

                                                    setUpdatedAccountIndexList((prev: any) =>
                                                        prev.includes(showAccount) ? prev : [...prev, showAccount]
                                                    );
                                                }
                                            }
                                        }}
                                        disabled={!editable}
                                    />
                                )}

                            </td>

                        </tr>
                        <tr className="readonly_bg">
                            <td className="pl-3">
                                Est Taxes
                                W/O Exemptions
                            </td>
                            <td title='(TotalYearTaxRate / TaxRateDivisor) * AppraisedAmount + TotalYearBaseTaxForZeroRateEntities'>
                                {assessmentsOverrideFormatting(curAccount, 0, 'woExemptionAmount', true, setName, setProductTypeInvisible, editable, "overrideWoExemptionAmount", null, setIsSavedResult, setRender, showAccount, setUpdatedAccountIndexList, setCertificate, certificate)}
                            </td>
                            <td title='(TotalYearTaxRate / TaxRateDivisor) * AppraisedAmount + TotalYearBaseTaxForZeroRateEntities'>
                                {assessmentsOverrideFormatting(curAccount, 1, 'woExemptionAmount', true, setName, setProductTypeInvisible, editable, "overrideWoExemptionAmount", null, setIsSavedResult, setRender, showAccount, setUpdatedAccountIndexList, setCertificate, certificate)}
                            </td>
                        </tr>


                        {pdfSettings && pdfSettings?.data?.showWithExemptionEstimate && showWee && (
                            <tr className="readonly_bg">
                                <td className="pl-3">
                                    With Exemption Estimate
                                </td>

                                {/* Ensure assessments array has at least 2 items */}
                                <td title='(PriorYearBaseTax / PriorYearAssessedValue) * CurrentYearBaseTax'>
                                    {curAccount?.assessments?.length > 0 &&
                                        assessmentsOverrideFormatting(curAccount, 0, 'withExemptionEstimate', true, setName, setProductTypeInvisible, editable, "withExemptionEstimateOverride", null, setIsSavedResult, setRender, showAccount, setUpdatedAccountIndexList, setCertificate, certificate)}
                                </td>
                                <td title="(PriorYearBaseTax / PriorYearAssessedValue) * CurrentYearBaseTax">
                                    {Array.isArray(curAccount?.assessments) && curAccount.assessments.length > 1
                                        ? assessmentsOverrideFormatting(
                                            curAccount,
                                            1,
                                            "withExemptionEstimate",
                                            true,
                                            setName,
                                            setProductTypeInvisible,
                                            editable,
                                            "withExemptionEstimateOverride",
                                            null,
                                            setIsSavedResult,
                                            setRender,
                                            showAccount,
                                            setUpdatedAccountIndexList,
                                            setCertificate,
                                            certificate
                                        )
                                        : null}
                                </td>

                            </tr>
                        )}
                        {(
                            (curAccount?.assessments?.[0]?.taxYear >= getCurrentTaxYear() && curAccount?.assessments[0]?.['certified'] === false)
                            || (curAccount?.assessments?.[1]?.taxYear >= getCurrentTaxYear() && curAccount?.assessments[1]?.['certified'] === false)
                        ) ? (
                            <>
                                <tr className="readonly_bg">
                                    <td colSpan={3} style={{ fontSize: '14px' }}>
                                        * NOTE: When calculating the Estimate W/O Exemption amounts for tax years where
                                        the Tax Value and/or the Tax Rate are Preliminary, this Estimate W/O Exemption
                                        amount is based on a combination of Preliminary Values for this tax year times
                                        the last Certified years Tax Rate, so it will not be exact final amount, and is
                                        provided for your information only
                                    </td>
                                </tr>
                            </>
                        ) : (
                            <>
                                {[...Array(2)].map(() => (
                                    <tr className="readonly_bg">
                                        <td className={"p-[9px] opacity-0"}>1</td>
                                        <td className={"p-[9px] opacity-0"}>1</td>
                                        <td className={"p-[9px] opacity-0"}>1</td>
                                    </tr>
                                ))}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AssessmentInfoSection;